import {
  ColorScale,
  definePreset,
  palette as wrongTypedPalette,
} from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';

const palette = wrongTypedPalette as (color: string) => ColorScale;

// Prime changed something internally in their theming solution, so all typing is messed up. We cannot add things that are not in the original theme.
// For now, just use `any` to avoid type errors.
export const CropxPreset = definePreset(Aura, {
  primitive: {
    purple: palette('#8F46C9'),
    blue: palette('#009CE2'),
    teal: palette('#05C3B4'),
    green: palette('#00B97B'),
    yellow: palette('#F9D915'),
    orange: palette('#FC8C10'),
    pink: palette('#FF1E65'),
    black: palette('#636464'),
    mustard: palette('#FAC34C'),
    salmon: palette('#F76647'),
  },
  semantic: {
    iconSize: '1.3rem',
    disabledOpacity: '0.4',
    primary: palette('{green}'),
    highlightFocusBackground: palette('{green.100}'),
    colorScheme: {
      light: {
        surface: palette('{neutral}'),
        text: {
          color: '{black.900}',
          hoverColor: '{black.950}',
          mutedColor: '{black.600}',
          hoverMutedColor: '{black.800}',
        },
        formField: {
          disabledBackground: '{surface.0}',
          disabledColor: '{text.color}',
        },
        highlight: {
          background: '{surface.50}',
          focusBackground: '{surface.100}',
          color: '{surface.700}',
          focusColor: '{surface.800}',
        },
      },
      dark: {
        surface: palette('{neutral}'),
        text: {
          color: '{black.50}',
          hoverColor: '{black.200}',
          mutedColor: '{black.200}',
          hoverMutedColor: '{black.300}',
        },
      },
    },
  },
  components: {
    progressspinner: {
      // A bug in primeuix causes these tokens to be overriden by the default ones.
      root: {
        colorOne: '{black.900} !important',
        colorTwo: '{black.900} !important',
        colorThree: '{black.900} !important',
        colorFour: '{black.900} !important',
      },
    },
    divider: {
      root: {
        borderColor: '{surface.100}',
      },
    },
    button: {
      css: () => `
        .p-button-tertiary {
          background: var(--p-black-900);
          color: var(--p-surface-0);
          border-color: var(--p-black-900);
          &:enabled {
            &:hover {
              color: var(--p-surface-0);
              background: #546e7a;
              border-color: #546e7a;
            }
          }
          &:disabled {
            color: var(--p-surface-0);
            background: var(--p-black-200);
            border-color: var(--p-black-200);
          }
        }
      `,
      root: {
        paddingX: '16px',
        paddingY: '10px',

        sm: {
          paddingX: '14px',
          paddingY: '8.75px',
          fontSize: '10.5px',
        },

        roundedBorderRadius: '3rem',
        borderRadius: '3rem',
        // fontFamily: 'Poppins',
        label: {
          fontWeight: '600',
        },
        // A bug in primeuix causes these tokens to be overriden by the default ones.
        secondary: {
          background: '{surface.0} !important',
          color: '{black.900} !important',
          borderColor: '{black.100} !important',
          hoverBackground: '{surface.0} !important',
          hoverBorderColor: '{black.500} !important',
        },
      },
      // A bug in primeuix causes these tokens to be overriden by the default ones.
      link: {
        color: '{primary.700} !important',
        hoverColor: '{primary.600} !important',
      },
      outlined: {
        // A bug in primeuix causes these tokens to be overriden by the default ones.
        plain: {
          borderColor: '{surface.300} !important',
          color: '{text.color} !important',
        },
      },
    },
    iconfield: {
      icon: {
        color: '{black.900}',
      },
    },
    select: {
      root: {
        filledHoverBackground: '{black.900}',
        focusBorderColor: '{black.900}',
        borderRadius: '10px',
        paddingX: '1rem',
        paddingY: '1rem',
      },
      option: {
        padding: '1.3rem',
        selectedColor: '{black.900}',
        selectedBackground: '#ffffff',
        selectedFocusBackground: '{black.50}',
        selectedFocusColor: '{black.900}',
        focusColor: '{black.900}',
      },

      dropdown: {
        color: '{black.900}',
        width: '4rem',
      },
      list: {
        padding: '0px',
      },
    },
    inputtext: {
      root: {
        paddingX: '1rem',
        paddingY: '1rem',
        borderRadius: '8px',
        focusBorderColor: '{black.900}',
      },
    },
    menu: {
      submenuLabel: {
        padding: '1rem',
      },
      item: {
        padding: '0.8rem',
        icon: {
          focusColor: '#6c757d',
          color: '#6c757d',
        },
      },
    },
    multiselect: {
      option: {
        padding: '1.3333rem',
      },
      root: {
        paddingX: '1rem',
        paddingY: '1rem',
        focusBorderColor: '{black.900}',
      },
      dropdown: {
        color: '#495057',
      },
    },
    checkbox: {
      root: {
        width: '20px',
        height: '20px',
        borderRadius: '10px',
        hoverBorderColor: '{black.900}',
        checkedBackground: '{black.900}',
        checkedBorderColor: '{black.900}',
        checkedHoverBackground: '{black.900}',
      },

      icon: {
        size: '14px',
      },
    },
    scrollpanel: {
      bar: {
        size: '9px',
      },
      colorScheme: {
        light: {
          bar: {
            background: '{black.300}',
          },
        },
      },
    },
    tieredmenu: {
      list: {
        gap: '1rem',
      },
      item: {
        gap: '2rem',
        icon: {
          color: '#6c757d',
        },
      },
    },
    datepicker: {
      inputIcon: {
        color: '{black.900}',
      },
      date: {
        selectedColor: '{black.900}',
        selectedBackground: '{primary.200}',
        rangeSelectedColor: '{black.900}',
        rangeSelectedBackground: '{primary.100}',
      },
    },
    inputgroup: {
      addon: {
        background: '{gray.100}',
        color: '{gray.500}',
      },
    },
    textarea: {
      root: {
        background: '#ffffff',
        color: '{black.900}',
        paddingX: '1rem',
        paddingY: '1rem',
        borderRadius: '8px',
        borderColor: '{gray.300}',
        focusBorderColor: '{black.900}',
      },
    },
    datatable: {
      header: {
        background: '#ececec',
        padding: '8px',
        borderColor: '#d2d3d3',
        borderWidth: '2px',
      },
      headerCell: {
        background: '#ececec',
        borderColor: '#d2d3d3',
        selectedBackground: '#ffffff',
        selectedColor: '{black.900}',
      },
      bodyCell: {
        borderColor: '#d2d3d3',
      },
    },
    toggleswitch: {
      root: {
        width: '3rem',
        height: '1.75rem',
        focusRing: {
          color: 'transparent',
        },
        checkedBackground: '{primary.500}',
      },
      handle: {
        size: '1.25rem',
      },
    },
    radiobutton: {
      root: {
        width: '20px',
        height: '20px',
        background: '#ffffff',
        borderColor: '#ced4da',
        hoverBorderColor: '{black.900}',
        checkedBackground: '#ffffff',
        checkedBorderColor: '{black.900}',
        checkedHoverBorderColor: '{black.900}',
        checkedHoverBackground: '#ffffff',
      },
      icon: {
        size: '12px',
        disabledColor: '{black.900}',
        checkedColor: '{black.900}',
        checkedHoverColor: '{black.500}',
      },
    },
    tag: {
      root: {
        borderRadius: '8px',
        roundedBorderRadius: '10rem',
      },
      icon: {
        size: '20px',
      },
    },
    listbox: {
      root: {
        borderColor: '#000000',
      },
      option: {
        padding: '0rem',
        selectedBackground: '{listbox.option.focus.background}',
        selectedFocusBackground: '{listbox.option.focus.background}',
      },
    },

    autocomplete: {},

    message: {
      warn: {
        color: '{salmon.800}',
        background: '{salmon.50}',
        borderColor: '{salmon.50}',
      },
    },

    password: {
      icon: {
        color: '{black.500}',
      },
    },
  },
});
